import { createErrorHandler } from "@/app-utils/errors.ts";
import { type AttributeValue, type InputTableQuerySpec, type Sample, type SampleStatus } from "@/graphql.ts";
import {
  LOAD_SAMPLE_FAILURE,
  LOAD_SAMPLE_REQUEST,
  LOAD_SAMPLE_STATUSES_BY_SET_FAILURE,
  LOAD_SAMPLE_STATUSES_BY_SET_REQUEST,
  LOAD_SAMPLE_STATUSES_BY_SET_SUCCESS,
  LOAD_SAMPLE_SUCCESS,
  SET_SAMPLE_LOCKING_FAILURE,
  SET_SAMPLE_LOCKING_REQUEST,
  SET_SAMPLE_LOCKING_SUCCESS,
} from "@/redux/action-types.ts";
import { reloadTableRows } from "@/redux/modules/table-data.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";

/**
 * State definition
 */
export interface ISampleState {
  sample: Sample;
  isLoading: boolean;
}

const initialState: ISampleState = {
  isLoading: false,
  sample: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_SAMPLE_REQUEST]: (state: ISampleState, _payload: Sample): ISampleState => {
    return {
      ...state,
      sample: null,
      isLoading: true,
    };
  },
  [LOAD_SAMPLE_SUCCESS]: (state: ISampleState, payload: Sample): ISampleState => {
    return {
      ...state,
      isLoading: false,
      sample: payload,
    };
  },
  [LOAD_SAMPLE_FAILURE]: (state: ISampleState): ISampleState => {
    return {
      ...state,
      isLoading: false,
      sample: null,
    };
  },
});

/**
 * Action creators
 */
export function shouldLoadSample(sampleId: number) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<Sample> => {
    const sampleState: ISampleState = getState().sample;

    // fetch the sample only if no loading is currently in progress and sample is either absent or doesn't match the sample ID
    if (!sampleState.isLoading) {
      dispatch({ type: LOAD_SAMPLE_REQUEST });

      return webApiClient.fetchSample(sampleId).then(
        (sample: Sample) => {
          dispatch({
            type: LOAD_SAMPLE_SUCCESS,
            payload: sample,
          });
          return sample;
        },
        createErrorHandler(dispatch, {
          actionType: LOAD_SAMPLE_FAILURE,
          alertTitle: "Fetching of Sample Failed",
        }),
      );
    } else {
      return Promise.resolve(sampleState.sample);
    }
  };
}

export function loadSampleValues(sampleId: number) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<AttributeValue[][]> => {
    return webApiClient.fetchSampleValues(sampleId).catch(
      createErrorHandler(dispatch, {
        actionType: LOAD_SAMPLE_FAILURE,
        alertTitle: "Fetching of Sample values failed",
      }),
    );
  };
}

export function loadSampleStatusesBySet(statusSetId: number) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<SampleStatus[]> => {
    dispatch({ type: LOAD_SAMPLE_STATUSES_BY_SET_REQUEST });

    return webApiClient.fetchSampleStatuses(statusSetId).then(
      (sampleStatuses: SampleStatus[]) => {
        dispatch({
          type: LOAD_SAMPLE_STATUSES_BY_SET_SUCCESS,
          payload: sampleStatuses,
        });
        return sampleStatuses;
      },
      createErrorHandler(dispatch, {
        actionType: LOAD_SAMPLE_STATUSES_BY_SET_FAILURE,
        showAlert: false,
        throwError: true,
      }),
    );
  };
}

export function setSampleLocking(sampleIds: number[], lockingAction: string, tableSpec: InputTableQuerySpec) {
  return (dispatch, _getState, webApiClient): Promise<boolean> => {
    dispatch({ type: SET_SAMPLE_LOCKING_REQUEST });

    return webApiClient
      .setSampleLocking(sampleIds, lockingAction)
      .then((result: boolean) => {
        dispatch({
          type: SET_SAMPLE_LOCKING_SUCCESS,
          payload: sampleIds,
        });

        // update the visible table with the current locking indicators
        dispatch(reloadTableRows(tableSpec));

        return result;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SET_SAMPLE_LOCKING_FAILURE,
          actionPayload: { sampleIds, lockingAction },
          alertTitle: "Locking Samples Failed",
        }),
      );
  };
}
